import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { getJobDetails, adminUpdateJob, createInvoiceAndRcti, getJobAuditLog, regenerateJobDocuments, checkNearbyCompletedJobs } from '../services/jobService';
import { getInvoiceDetails, voidInvoice } from '../services/invoiceService';
import { getRCTIDetails, voidRCTI } from '../services/rctiService';
import { getXeroStatusPillClass } from '../utils/xeroUtils';
import { searchFranchisees } from '../services/franchiseeService';
import { listServiceTypes } from '../services/serviceTypeService';
import { debounce } from 'lodash';
import { FaMapMarkerAlt, FaCheck, FaExclamationTriangle, FaCalendarCheck, FaInfoCircle, FaPencilAlt, FaMedkit, FaCalendarAlt, FaFileInvoice, FaTrashAlt, FaSync } from 'react-icons/fa';
import { terminology, allocationTypes, formatters } from '../config/terminology';
import { useAppConfig } from '../context/AppConfigContext';
import CompletionProgressModal from '../components/CompletionProgressModal';
import ConfirmationModal from '../components/ConfirmationModal';
import RegenerateDocsModal from '../components/RegenerateDocsModal';
import DuplicateJobWarningModal from '../components/DuplicateJobWarningModal';
import AllocationOverrideModal from '../components/AllocationOverrideModal';
import VoidNoticeModal from '../components/VoidNoticeModal';
import { useVoidNotice } from '../hooks/useVoidNotice';
import ActivityLog from '../components/ActivityLog';

const formatTimestampForInput = (timestamp) => {
    if (!timestamp || !timestamp._seconds) return '';
    const date = new Date(timestamp._seconds * 1000);
    const timezoneOffset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() - timezoneOffset);
    return localDate.toISOString().split('T')[0];
};

const formatTimestampForDisplay = (timestamp, includeTime = false) => {
    if (!timestamp || !timestamp._seconds) return 'N/A';
    const date = new Date(timestamp._seconds * 1000);
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    if (includeTime) {
        options.hour = '2-digit';
        options.minute = '2-digit';
        options.hour12 = true;
    }
    return date.toLocaleDateString('en-AU', options);
};

const parseInvoiceGenerationError = (rawError) => {
    if (!rawError || typeof rawError !== 'string') {
        return {
            failedTypes: [],
            technicalMessages: []
        };
    }

    const technicalMessages = rawError
        .split(';')
        .map(part => part.trim())
        .filter(Boolean);

    const failedTypes = [];

    technicalMessages.forEach((message) => {
        const normalized = message.toLowerCase();

        if (normalized.startsWith('invoice:')) {
            failedTypes.push('invoice');
        } else if (normalized.startsWith('rcti:')) {
            failedTypes.push('rcti');
        }
    });

    return {
        failedTypes: [...new Set(failedTypes)],
        technicalMessages
    };
};

const useSearch = (searchFunction, options = {}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const optionsString = JSON.stringify(options);

    const debouncedFetch = useCallback(debounce(async (term, fetchOptions) => {
        if (!searchFunction || term.length < 3) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }
        setIsSearching(true);
        try {
            const data = await searchFunction({ searchString: term, ...fetchOptions });
            const results = data.franchisees || data.clients || data.households || [];
            setSuggestions(results);
            setShowSuggestions(true);
        } catch (error) {
            console.error("Search error:", error);
            setSuggestions([]);
            setShowSuggestions(false);
        } finally {
            setIsSearching(false);
        }
    }, 300), [searchFunction]);

    useEffect(() => {
        const currentOptions = JSON.parse(optionsString);
        debouncedFetch(searchTerm, currentOptions);
        return () => debouncedFetch.cancel();
    }, [searchTerm, optionsString, debouncedFetch]);

    return { searchTerm, setSearchTerm, suggestions, setSuggestions, isSearching, showSuggestions, setShowSuggestions };
};

const today = new Date();
today.setHours(0, 0, 0, 0);
const todayTimestampSeconds = Math.floor(today.getTime() / 1000);

// Edits to a completed job that change what it bills, so its draft invoice/RCTI has to be reissued.
// Completion date is one of them: moving it across a price effective date changes the rate billed.
const REGEN_TRIGGER_LABELS = {
    pricePerHour: 'price',
    additionalPrice: 'price',
    invoicePricePerHour: 'price',
    actualHours: 'actual hours',
    allotedHours: 'allotted hours',
    completionDate: 'completion date',
};

// "price and actual hours" — the three price fields collapse to one label.
const describeRegenTriggers = (fields) => {
    const labels = [...new Set(fields.map(f => REGEN_TRIGGER_LABELS[f]).filter(Boolean))];
    if (labels.length === 0) return 'change';
    const phrase = labels.length === 1
        ? labels[0]
        : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
    return `${phrase} change`;
};

// Manual RCTI generation emails the franchisee; this line tells the admin whether it actually went, and where.
const RctiEmailOutcome = ({ email }) => {
    if (!email) return null;
    if (email.status === 'sent') {
        return <p className="text-sm text-green-700">✓ RCTI emailed to {email.address}</p>;
    }
    if (email.status === 'queued') {
        return <p className="text-sm text-gray-600">RCTI email queued to {email.address}, delivery not yet confirmed</p>;
    }
    if (email.status === 'skipped') {
        return (
            <p className="text-sm text-amber-700">
                RCTI email not sent: {email.reason || 'skipped'}{email.address ? ` (would have gone to ${email.address})` : ''}
            </p>
        );
    }
    return <p className="text-sm text-red-700">✗ RCTI email to {email.address || 'franchisee'} failed: {email.reason || 'unknown error'}</p>;
};

const EditJobPage = () => {
    // Confirm who to tell after a manual void; the void itself no longer emails.
    const { openVoidNotice, voidNoticeProps } = useVoidNotice();
    const { id: jobId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const filtersFromNav = location.state?.filters || {}; // Legacy: filters passed via nav state
    // Where "back" goes. Callers now hand over an explicit returnTo (pathname + query string);
    // returnSearch is the older Invoices-list convention and still honoured.
    const navReturnTo = location.state?.returnTo || null;
    const returnSearch = location.state?.returnSearch || null;
    const [apiError, setApiError] = useState(null);
    const [saveSuccessMessage, setSaveSuccessMessage] = useState(null);
    const [completionWarnings, setCompletionWarnings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [originalJobData, setOriginalJobData] = useState(null);

    const [allServiceTypes, setAllServiceTypes] = useState([]);
    const [serviceTypesLoading, setServiceTypesLoading] = useState(true);
    const [selectedServiceIds, setSelectedServiceIds] = useState([]);
    const [serviceSelectionError, setServiceSelectionError] = useState(null);
    const [statusChangeValidationError, setStatusChangeValidationError] = useState(null);
    const errorRef = useRef(null);
    const [showInvoiceRctiModal, setShowInvoiceRctiModal] = useState(false);
    const [invoiceRctiCreating, setInvoiceRctiCreating] = useState(false);
    const [invoiceRctiResult, setInvoiceRctiResult] = useState(null);
    const [showCompletionProgress, setShowCompletionProgress] = useState(false);
    const [completionStep, setCompletionStep] = useState('saving');
    const [completionStepResults, setCompletionStepResults] = useState({});
    const [invoiceVoided, setInvoiceVoided] = useState(false);
    const [rctiVoided, setRctiVoided] = useState(false);

    // Void invoice/RCTI state
    const [invoiceStatus, setInvoiceStatus] = useState(null);
    const [rctiStatus, setRctiStatus] = useState(null);
    // Live Xero invoice status from the authoritative invoice doc (the job's copy is best-effort)
    const [invoiceXeroStatus, setInvoiceXeroStatus] = useState(null);
    const [voidingInvoice, setVoidingInvoice] = useState(false);
    const [voidingRCTI, setVoidingRCTI] = useState(false);
    const [showVoidInvoiceConfirm, setShowVoidInvoiceConfirm] = useState(false);
    const [showVoidRCTIConfirm, setShowVoidRCTIConfirm] = useState(false);

    // Regenerate invoice/RCTI after a completed job's price, hours or completion date is edited
    const [showRegenModal, setShowRegenModal] = useState(false);
    const [regenLoading, setRegenLoading] = useState(false);
    const [regenPreview, setRegenPreview] = useState(null);
    const [regenSelection, setRegenSelection] = useState({ invoice: false, rcti: false });
    const [regenSubmitting, setRegenSubmitting] = useState(false);
    const [regenResult, setRegenResult] = useState(null);
    const [regenChangeSummary, setRegenChangeSummary] = useState(null);

    // Possible-duplicate warning shown before an admin completes a job
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [allocationBreach, setAllocationBreach] = useState(null);
    const [pendingAllocationData, setPendingAllocationData] = useState(null);
    const [duplicateJobs, setDuplicateJobs] = useState([]);
    const [pendingCompletionData, setPendingCompletionData] = useState(null);

    const [auditLog, setAuditLog] = useState([]);
    const [auditLogLoading, setAuditLogLoading] = useState(false);
    const [auditLogExpanded, setAuditLogExpanded] = useState(false);

    const { loading: configLoading, error: configError, getStatuses } = useAppConfig();
    const jobStatuses = useMemo(() => getStatuses() || [], [getStatuses]);

    const { register, handleSubmit, watch, setValue, reset, formState: { errors, isSubmitting, isDirty } } = useForm({
        mode: 'onBlur',
        defaultValues: {
            fseId: '',
            fseNameSearch: '',
            clientName: '',
            householdName: '',
            householdAddress: '',
            jobDate: '',
            jobTime: '',
            allocationType: '',
            allotedHours: '',
            actualHours: '',
            pricePerHour: '',
            additionalPrice: '',
            priceQuotePerHour: '',
            invoicePricePerHour: '',
            status: '',
            completionDate: '',
            description: '',
            adminNotes: '',
            notesForFranchisee: '',
            servicesDirty: false,
        }
    });

    const allocationType = watch('allocationType');
    const pricePerHour = watch('pricePerHour');
    const currentStatus = watch('status');
    const additionalPrice = watch('additionalPrice');

    // Frequency the allotted hours are measured against. Read from the raw job rather than the
    // form, which only knows the collapsed 'fixed-hours' UI type and not monthly/days/weeks.
    const allocationFrequencyLabel = useMemo(() => {
        const rawType = originalJobData?.allocationType;
        if (!['monthly', 'days', 'weeks'].includes(rawType)) return '';
        const interval = originalJobData?.allocationValue ?? originalJobData?.frequencyValue;
        return formatters.formatFrequency(rawType, interval);
    }, [originalJobData]);

    const showAllocationFrequency = allocationType === 'fixed-hours' && !!allocationFrequencyLabel;

    // Hours are small values, so the native spinners are noise — especially crowded against the
    // frequency addon. Shared by both hours fields so they stay identical.
    const hoursInputClass = 'block w-full input-field focus:outline-none '
        + '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';

    const isOverdue = useMemo(() => {
        if (!originalJobData) return false;
        return originalJobData.status === 'approved' &&
               originalJobData.jobDate?._seconds &&
               originalJobData.jobDate._seconds < todayTimestampSeconds;
    }, [originalJobData]);

    const isServiceDelayed = useMemo(() => {
        return originalJobData?.status === 'service-delayed';
    }, [originalJobData]);

    const invoiceGenerationFailureInfo = useMemo(() => {
        if (originalJobData?.invoiceGenerationStatus !== 'failed') {
            return null;
        }

        const { failedTypes, technicalMessages } = parseInvoiceGenerationError(originalJobData.invoiceGenerationError);

        let userFriendlyMessage = 'Invoice/RCTI generation failed. Please try again using the button above, or contact support.';

        if (failedTypes.length === 2) {
            userFriendlyMessage = 'Invoice and RCTI generation failed. Please try again using the button above, or contact support.';
        } else if (failedTypes[0] === 'invoice') {
            userFriendlyMessage = 'Invoice generation failed. Please try again using the button above, or contact support.';
        } else if (failedTypes[0] === 'rcti') {
            userFriendlyMessage = 'RCTI generation failed. Please try again using the button above, or contact support.';
        }

        return {
            userFriendlyMessage,
            technicalMessages
        };
    }, [originalJobData]);

    const invoiceRctiActionLabel = useMemo(() => {
        if (originalJobData?.invoiceId && !originalJobData?.rctiId) {
            return 'Create RCTI Only';
        }

        if (!originalJobData?.invoiceId && originalJobData?.rctiId) {
            return 'Create Invoice Only';
        }

        return 'Create Invoice & RCTI';
    }, [originalJobData]);

    const fetchJob = useCallback(async () => {
        setLoading(true);
        setApiError(null);
        setSaveSuccessMessage(null);
        try {
            const jobData = await getJobDetails(jobId);
            setOriginalJobData(jobData);

            const contactName = `${jobData.franchiseeInfo?.firstName || ''} ${jobData.franchiseeInfo?.lastName || ''}`.trim();
            const fseDisplayName = `${jobData.franchiseeInfo?.franchiseName || 'Unknown Franchise'}${contactName ? ` - ${contactName}` : ''}`;
            const householdDisplayName = `${jobData.householdInfo?.firstName || ''} ${jobData.householdInfo?.lastName || ''}`.trim() || 'N/A';
            const householdAddress = `${jobData.householdInfo?.addressLine1 || ''}${jobData.householdInfo?.addressLine2 ? ', ' + jobData.householdInfo.addressLine2 : ''}, ${jobData.householdInfo?.state || ''} ${jobData.householdInfo?.postalCode || ''}`.replace(/^, /,'').replace(/, $/,'').trim() || 'No address';

            const initialServiceIds = Array.isArray(jobData.services)
                ? jobData.services.map(s => s.serviceId || s.serviceTypeId).filter(Boolean)
                : [];
            setSelectedServiceIds(initialServiceIds);

            // Jobs inherit allocation type from schedule - just map backend values to UI values
            let allocationTypeUI = 'fixed-fee'; // Default UI allocation type
            
            if (jobData.allocationType === 'set-fee' || jobData.allocationType === 'fixed-fee') {
                allocationTypeUI = 'fixed-fee';
            } else if (jobData.allocationType === 'monthly' || jobData.allocationType === 'days' || jobData.allocationType === 'weeks' || jobData.allocationType === 'fixed-hours') {
                allocationTypeUI = 'fixed-hours';
            }

            reset({
                fseId: jobData.fseId || '',
                fseNameSearch: fseDisplayName,
                clientName: jobData.clientInfo?.clientName || 'N/A',
                householdName: householdDisplayName,
                householdAddress: householdAddress,
                jobDate: formatTimestampForInput(jobData.jobDate),
                jobTime: jobData.jobTime || '',
                allocationType: allocationTypeUI,
                allotedHours: jobData.allotedHours !== null ? String(jobData.allotedHours) : '',
                actualHours: jobData.actualHours !== null ? String(jobData.actualHours) : '',
                pricePerHour: jobData.pricePerHour !== null ? String(jobData.pricePerHour) : '',
                additionalPrice: jobData.additionalPrice !== null ? String(jobData.additionalPrice) : '',
                priceQuotePerHour: jobData.priceQuotePerHour !== null ? String(jobData.priceQuotePerHour) : '',
                invoicePricePerHour: jobData.invoicePricePerHour !== null ? String(jobData.invoicePricePerHour) : '',
                status: jobData.status || '',
                completionDate: formatTimestampForInput(jobData.completionDate),
                description: jobData.description || '',
                adminNotes: jobData.adminNotes || '',
                notesForFranchisee: jobData.notesForFranchisee || '',
                servicesDirty: false,
            }, { keepValues: false });

        } catch (err) {
            console.error("Failed to fetch job details:", err);
            setApiError(err.message || 'Failed to load job details.');
            setOriginalJobData(null);
        } finally {
            setLoading(false);
        }
    }, [jobId, reset]);

    useEffect(() => {
        fetchJob();
    }, [fetchJob]);

    useEffect(() => {
        const fetchServices = async () => {
            setServiceTypesLoading(true);
            try {
                const services = await listServiceTypes();
                setAllServiceTypes(services);
            } catch (error) {
                console.error("Failed to fetch service types:", error);
                setApiError("Could not load service types. Please try again later.");
            } finally {
                setServiceTypesLoading(false);
            }
        };
        fetchServices();
    }, []);

    useEffect(() => {
        const additionalPriceString = additionalPrice;
        const isNdisJob = originalJobData?.ndisJob || false;

        if (additionalPriceString !== null && additionalPriceString !== undefined && additionalPriceString !== '' && !isNaN(parseFloat(additionalPriceString))) {
            const invoicePriceExGST = parseFloat(additionalPriceString);

            if (invoicePriceExGST >= 0) {
                // For NDIS jobs, don't add GST; for non-NDIS jobs, add 10% GST
                const invoicePriceWithGST = isNdisJob 
                    ? invoicePriceExGST.toFixed(2) 
                    : (invoicePriceExGST * 1.1).toFixed(2);
                setValue('invoicePricePerHour', invoicePriceWithGST, { shouldValidate: true, shouldDirty: true });
            } else {
                setValue('invoicePricePerHour', '', { shouldValidate: true, shouldDirty: true });
            }
        } else if (additionalPriceString === '' || additionalPriceString === null || additionalPriceString === undefined) {
             setValue('invoicePricePerHour', '', { shouldValidate: true, shouldDirty: true });
        }
    }, [additionalPrice, setValue, originalJobData?.ndisJob]);

    useEffect(() => {
        let timer;
        if (saveSuccessMessage) {
          timer = setTimeout(() => {
            setSaveSuccessMessage(null);
          }, 5000);
        }
        return () => clearTimeout(timer);
      }, [saveSuccessMessage]);

    useEffect(() => {
        if ((statusChangeValidationError || apiError) && errorRef.current) {
            errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [statusChangeValidationError, apiError]);

    const fetchAuditLog = useCallback(async () => {
        setAuditLogLoading(true);
        try {
            const entries = await getJobAuditLog(jobId);
            setAuditLog(entries);
        } catch (err) {
            console.error('Failed to fetch audit log:', err);
            setAuditLog([]);
        } finally {
            setAuditLogLoading(false);
        }
    }, [jobId]);

    useEffect(() => {
        fetchAuditLog();
    }, [fetchAuditLog]);

    useEffect(() => {
        const fetchVoidStatus = async () => {
            const invoiceId = originalJobData?.invoiceId;
            const rctiId = originalJobData?.rctiId;

            if (invoiceId) {
                try {
                    const invoiceData = await getInvoiceDetails(invoiceId);
                    setInvoiceVoided(invoiceData.status === 'void' || invoiceData.xero_invoice_status === 'VOIDED');
                } catch (err) {
                    console.error('[EditJobPage] Failed to fetch invoice void status:', err);
                    setInvoiceVoided(false);
                }
            } else {
                setInvoiceVoided(false);
            }

            if (rctiId) {
                try {
                    const rctiData = await getRCTIDetails(rctiId);
                    setRctiVoided(rctiData.status === 'void' || rctiData.xero_invoice_status === 'VOIDED');
                } catch (err) {
                    console.error('[EditJobPage] Failed to fetch RCTI void status:', err);
                    setRctiVoided(false);
                }
            } else {
                setRctiVoided(false);
            }
        };

        fetchVoidStatus();
    }, [originalJobData?.invoiceId, originalJobData?.rctiId]);

    // Fetch invoice/RCTI status when job data loads
    useEffect(() => {
        const fetchInvoiceRctiStatus = async () => {
            if (originalJobData?.invoiceId) {
                try {
                    const invoiceData = await getInvoiceDetails(originalJobData.invoiceId);
                    setInvoiceStatus(invoiceData?.status || null);
                    setInvoiceXeroStatus(invoiceData?.xero_invoice_status || null);
                } catch (err) {
                    console.error('Failed to fetch invoice status:', err);
                }
            }
            if (originalJobData?.rctiId) {
                try {
                    const rctiData = await getRCTIDetails(originalJobData.rctiId);
                    setRctiStatus(rctiData?.status || null);
                } catch (err) {
                    console.error('Failed to fetch RCTI status:', err);
                }
            }
        };
        fetchInvoiceRctiStatus();
    }, [originalJobData?.invoiceId, originalJobData?.rctiId]);

    const handleVoidInvoice = async () => {
        setShowVoidInvoiceConfirm(false);
        setVoidingInvoice(true);
        try {
            const response = await voidInvoice(originalJobData.invoiceId);
            setInvoiceStatus('void');
            setSaveSuccessMessage(response?.message || 'Invoice voided successfully.');
            fetchJob(); // Refresh job data
            // The void no longer emails on its own — ask who, if anyone, should be told.
            openVoidNotice(response);
        } catch (err) {
            console.error('Error voiding invoice:', err);
            setApiError(err.message || 'Failed to void invoice.');
        } finally {
            setVoidingInvoice(false);
        }
    };

    const handleVoidRCTI = async () => {
        setShowVoidRCTIConfirm(false);
        setVoidingRCTI(true);
        try {
            await voidRCTI(originalJobData.rctiId);
            setRctiStatus('void');
            setSaveSuccessMessage('RCTI voided successfully.');
            fetchJob(); // Refresh job data
        } catch (err) {
            console.error('Error voiding RCTI:', err);
            setApiError(err.message || 'Failed to void RCTI.');
        } finally {
            setVoidingRCTI(false);
        }
    };

    const isInvoiceVoided = invoiceStatus === 'void';
    const isRctiVoided = rctiStatus === 'void';
    // Prefer the live invoice's Xero status; fall back to the job's denormalized copy.
    const displayXeroInvoiceStatus = invoiceXeroStatus || originalJobData?.xero_invoice_status || null;
    const canVoidInvoice = originalJobData?.invoiceId && invoiceStatus && invoiceStatus !== 'void' && invoiceStatus !== 'paid';
    const canVoidRCTI = originalJobData?.rctiId && rctiStatus && rctiStatus !== 'void' && rctiStatus !== 'paid';

    // Hand the invoice/RCTI screens an explicit way back to this job, carrying the jobs-list
    // filters/sorting this page was opened with so the whole chain survives the round trip.
    const documentReturnState = {
        // Replay whatever state brought us here, so the chain back to the list stays intact.
        returnTo: { pathname: location.pathname, state: location.state || {} },
        returnLabel: 'Back to Job',
    };

    const backTarget = navReturnTo
        ? { pathname: navReturnTo.pathname, search: navReturnTo.search || '' }
        : returnSearch
            ? { pathname: '/invoices', search: returnSearch }
            : { pathname: '/jobs-list' };
    const backLabel = location.state?.returnLabel || (returnSearch ? 'Back to Invoices' : 'Back to Jobs List');
    // Only the legacy path still needs a filters payload; returnTo carries them in the query string.
    const backState = navReturnTo ? {} : (returnSearch ? {} : { filters: filtersFromNav });

    const franchiseeSearch = useSearch(searchFranchisees);

    const fseHandlers = {
        onChange: (e) => {
            const value = e.target.value;
            franchiseeSearch.setSearchTerm(value);
            setValue('fseNameSearch', value, { shouldDirty: true });
            setValue('fseId', '', { shouldDirty: true });
            if (value.length < 3) franchiseeSearch.setShowSuggestions(false);
        },
        onSelect: (item) => {
            setValue('fseId', item.id, { shouldDirty: true });
            const contactName = `${item.firstName || ''} ${item.lastName || ''}`.trim();
            const displayName = `${item.franchiseName || 'Unknown Franchise'}${contactName ? ` - ${contactName}` : ''}`;
            setValue('fseNameSearch', displayName, { shouldDirty: true });
            franchiseeSearch.setSuggestions([]);
            franchiseeSearch.setShowSuggestions(false);
        },
        renderSuggestions: (item) => {
            const contactName = `${item.firstName || ''} ${item.lastName || ''}`.trim();
            return `${item.franchiseName || 'Unknown Franchise'}${contactName ? ` - ${contactName}` : ''}`;
        }
    };

    const handleServiceToggle = (serviceId) => {
        setSelectedServiceIds(prev =>
            prev.includes(serviceId)
                ? prev.filter(id => id !== serviceId)
                : [...prev, serviceId]
        );
        setServiceSelectionError(null);
        setValue('servicesDirty', true, { shouldDirty: true });
    };

    // skipDuplicateCheck is set when the admin has already seen and dismissed the duplicate warning.
    const onSubmit = async (data, skipDuplicateCheck = false, allocationOverride = false) => {
        setApiError(null);
        setServiceSelectionError(null);
        setStatusChangeValidationError(null);
        setSaveSuccessMessage(null);

        if (selectedServiceIds.length === 0) {
            setServiceSelectionError("Please select at least one service.");
            return;
        }

        const originalStatus = originalJobData?.status;
        const newStatus = data.status;
        const fsePrice = data.pricePerHour;

        if (originalStatus === 'pending-approval' && newStatus === 'approved') {
            if (fsePrice === null || fsePrice === undefined || fsePrice === '' || isNaN(parseFloat(fsePrice)) || parseFloat(fsePrice) < 0) {
                setStatusChangeValidationError('FSE price/hour is required when approving a job.');
                return;
            }
        }

        if (newStatus === 'completed' && (!data.completionDate || data.completionDate === '')) {
            setStatusChangeValidationError('Completion date is required when setting status to completed.');
            return;
        }

        if (newStatus === 'completed' && data.completionDate) {
            // Ensure the completion date is not a weekend
            const selectedDate = new Date(data.completionDate);
            const dayOfWeek = selectedDate.getDay();
            if (dayOfWeek === 0 || dayOfWeek === 6) {
                setStatusChangeValidationError('Completion date cannot fall on a weekend.');
                return;
            }
        }

        if (newStatus === 'completed' && originalJobData?.allocationType !== 'fixed-fee') {
            const actualHoursVal = parseFloat(data.actualHours);
            if (isNaN(actualHoursVal) || actualHoursVal <= 0) {
                setStatusChangeValidationError('Actual Hours are required and must be greater than 0 when completing a job.');
                return;
            }
        }

        const serviceNamesMap = new Map(allServiceTypes.map(service => [service.id, service.name]));
        const servicesArray = selectedServiceIds.map(id => ({ serviceTypeId: id, name: serviceNamesMap.get(id) })).filter(s => s.name);

        if (servicesArray.length !== selectedServiceIds.length) {
             setServiceSelectionError("Could not find names for all selected services. Please try again.");
             console.error("Failed to map service IDs to names:", selectedServiceIds, serviceNamesMap);
             return;
        }

        const updatePayload = allocationOverride ? { allocationOverride: true } : {};
        const originalServices = (originalJobData?.services || []).map(s => s.serviceId || s.serviceTypeId).filter(Boolean).sort();
        const currentServices = [...selectedServiceIds].sort();

        // Transform allocation type: Jobs don't change allocation type - they inherit from schedule
        // But we need to maintain the backend value format
        let finalAllocationType = originalJobData?.allocationType; // Keep original backend value
        
        // Only allow UI changes for display, but don't transform for jobs
        if (data.allocationType !== 'fixed-fee' && data.allocationType !== 'fixed-hours') {
            // If somehow an invalid UI value gets through, keep original
            finalAllocationType = originalJobData?.allocationType || 'fixed-fee';
        }

        if (data.fseId !== originalJobData?.fseId) updatePayload.fseId = data.fseId;
        if (data.jobDate !== formatTimestampForInput(originalJobData?.jobDate)) updatePayload.jobDate = data.jobDate;
        if (data.jobTime !== (originalJobData?.jobTime || '')) updatePayload.jobTime = data.jobTime || null;
        // Note: We don't update allocationType for individual jobs - they inherit from schedule
        if (data.status !== originalJobData?.status) updatePayload.status = data.status;
        if (data.completionDate !== formatTimestampForInput(originalJobData?.completionDate)) updatePayload.completionDate = data.completionDate || null;
        if (data.description !== (originalJobData?.description || '')) updatePayload.description = data.description || null;
        if (data.adminNotes !== (originalJobData?.adminNotes || '')) updatePayload.adminNotes = data.adminNotes || null;
        if (data.notesForFranchisee !== (originalJobData?.notesForFranchisee || '')) updatePayload.notesForFranchisee = data.notesForFranchisee || null;

        const allottedHoursNum = data.allotedHours ? parseFloat(data.allotedHours) : null;
        if (allottedHoursNum !== originalJobData?.allotedHours) updatePayload.allotedHours = allottedHoursNum;

        const actualHoursNum = data.actualHours ? parseFloat(data.actualHours) : null;
        if (actualHoursNum !== originalJobData?.actualHours) updatePayload.actualHours = actualHoursNum;

        const pricePerHourNum = data.pricePerHour ? parseFloat(data.pricePerHour) : null;
        if (pricePerHourNum !== originalJobData?.pricePerHour || (originalStatus === 'pending-approval' && newStatus === 'approved')) {
             updatePayload.pricePerHour = pricePerHourNum;
        }

        const additionalPriceNum = data.additionalPrice ? parseFloat(data.additionalPrice) : null;
        if (additionalPriceNum !== originalJobData?.additionalPrice) updatePayload.additionalPrice = additionalPriceNum;

        const invoicePricePerHourNum = data.invoicePricePerHour ? parseFloat(data.invoicePricePerHour) : null;
        if (invoicePricePerHourNum !== originalJobData?.invoicePricePerHour) updatePayload.invoicePricePerHour = invoicePricePerHourNum;

        if (JSON.stringify(currentServices) !== JSON.stringify(originalServices)) {
            updatePayload.services = servicesArray;
        }

        if (Object.keys(updatePayload).length === 0) {
            setApiError("No changes detected.");
            return;
        }

        console.log("Submitting update payload:", updatePayload);
        console.log("adminUpdateJob parameters - jobId:", jobId, "updatePayload:", updatePayload);

        // Detect if this is a completion action (status changing to 'completed')
        const isCompletionAction = updatePayload.status === 'completed' && originalJobData?.status !== 'completed';

        // Same duplicate check the franchisee app runs on completion. Advisory: it surfaces the
        // conflict and lets the admin decide, matching the duplicate-schedule warning on Add Job Schedule.
        if (isCompletionAction && !skipDuplicateCheck) {
            const duplicates = await checkNearbyCompletedJobs({
                jobId,
                fseId: originalJobData?.fseId,
                householdId: originalJobData?.householdId,
                scheduleId: originalJobData?.scheduleId,
                services: originalJobData?.services,
                proposedCompletionDate: updatePayload.completionDate || formatTimestampForInput(originalJobData?.completionDate),
                frequencyType: originalJobData?.frequencyType,
                frequencyValue: originalJobData?.frequencyValue,
            });
            if (duplicates.length > 0) {
                setDuplicateJobs(duplicates);
                setPendingCompletionData(data);
                setShowDuplicateModal(true);
                return;
            }
        }

        if (isCompletionAction) {
            // Show stepper modal for completion
            setShowCompletionProgress(true);
            setCompletionStep('saving');
            setCompletionStepResults({ saving: { status: 'in-progress' } });
            setCompletionWarnings([]);
        }

        try {
            const response = await adminUpdateJob(jobId, updatePayload);
            console.log("adminUpdateJob response:", response);

            let saveMessage = null;
            if (isCompletionAction) {
                // Update stepper: saving done
                const newResults = { saving: { status: 'success' } };

                // Check if invoice/RCTI results were returned (nested under response.data)
                const jobResponseData = response.data || {};
                if (jobResponseData.invoiceRctiResults) {
                    // Invoice step
                    if (jobResponseData.invoiceRctiResults.invoice) {
                        newResults.invoice = jobResponseData.invoiceRctiResults.invoice;
                    } else {
                        newResults.invoice = { status: 'skipped' };
                    }
                    // RCTI step
                    if (jobResponseData.invoiceRctiResults.rcti) {
                        newResults.rcti = jobResponseData.invoiceRctiResults.rcti;
                    } else {
                        newResults.rcti = { status: 'skipped' };
                    }
                } else if (jobResponseData.invoiceRctiErrors && jobResponseData.invoiceRctiErrors.length > 0) {
                    // Had errors but no results — check what failed
                    const invoiceError = jobResponseData.invoiceRctiErrors.find(e => e.type === 'Invoice' || e.type === 'Monthly Allocation Check');
                    const rctiError = jobResponseData.invoiceRctiErrors.find(e => e.type === 'RCTI');
                    newResults.invoice = invoiceError
                        ? { status: 'failed', message: invoiceError.message }
                        : { status: 'skipped' };
                    newResults.rcti = rctiError
                        ? { status: 'failed', message: rctiError.message }
                        : { status: 'skipped' };
                } else {
                    // No invoice/RCTI results — likely monthly allocation in progress or carwash
                    newResults.invoice = { status: 'skipped' };
                    newResults.rcti = { status: 'skipped' };
                }

                setCompletionWarnings(jobResponseData.xeroSyncWarnings || []);
                setCompletionStepResults(newResults);
                setCompletionStep('done');
            } else {
                // Non-completion update — build message to show after re-fetch
                saveMessage = `Job ${jobId} updated successfully!`;
                if (response.status === 'completed' && response?.allocationType === 'monthly') {
                    saveMessage += ' Invoice and RCTI will be generated at the end of the month.';
                }
            }

            await fetchJob();
            fetchAuditLog();
            if (saveMessage) {
                setSaveSuccessMessage(saveMessage);
            }

            // If an edit changes what a completed job bills, offer to regenerate its existing draft
            // invoice/RCTI (reusing the number + (n) suffix). Completion date counts: moving it
            // across a price effective date changes the rate the job bills at.
            if (!isCompletionAction) {
                const changed = Object.keys(REGEN_TRIGGER_LABELS).filter(
                    f => Object.prototype.hasOwnProperty.call(updatePayload, f)
                );
                const staysCompleted = originalJobData?.status === 'completed' && (updatePayload.status === undefined || updatePayload.status === 'completed');
                const hasDocs = !!(originalJobData?.invoiceId || originalJobData?.rctiId);
                if (changed.length > 0 && staysCompleted && hasDocs) {
                    setRegenChangeSummary(describeRegenTriggers(changed));
                    openRegenPreview();
                }
            }
        } catch (error) {
            // Over the client's monthly allocation. Offer the override rather than a dead end.
            const breach = error?.response?.status === 409 ? error.response.data?.data : null;
            if (breach?.canOverride && !allocationOverride) {
                setAllocationBreach({ ...breach.allocationBreach, message: error.message });
                setPendingAllocationData(data);
                if (isCompletionAction) setCompletionStep(null);
                console.error("Submission blocked by monthly allocation:", error);
                return;
            }
            if (isCompletionAction) {
                setCompletionStepResults(prev => ({
                    ...prev,
                    saving: { status: 'failed', message: error.message || 'Failed to update job' },
                    invoice: { status: 'skipped' },
                    rcti: { status: 'skipped' },
                }));
                setCompletionWarnings([]);
                setCompletionStep('done');
            } else {
                setApiError(error.message || 'Failed to update job. Please check details.');
            }
            console.error("Submission error:", error);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && e.target.tagName === 'INPUT') {
            e.preventDefault();
        }
    };

    const handleCreateInvoiceAndRcti = async () => {
        setInvoiceRctiCreating(true);
        setInvoiceRctiResult(null);
        setApiError(null);
        
        try {
            const response = await createInvoiceAndRcti(jobId);
            setInvoiceRctiResult(response);
            
            // If successful or partial, refresh the job data
            if (response.status === 'success' || response.status === 'partial') {
                await fetchJob();
            }
        } catch (error) {
            setApiError(error.message || `Failed to ${invoiceRctiActionLabel.toLowerCase()}`);
            setInvoiceRctiResult({ status: 'fail', message: error.message });
        } finally {
            setInvoiceRctiCreating(false);
        }
    };

    const openInvoiceRctiModal = () => {
        setShowInvoiceRctiModal(true);
        setInvoiceRctiResult(null);
    };

    const closeInvoiceRctiModal = () => {
        setShowInvoiceRctiModal(false);
        setInvoiceRctiResult(null);
    };

    // --- Regenerate invoice/RCTI after a completed job's price change ---
    const openRegenPreview = async () => {
        setRegenResult(null);
        setRegenPreview(null);
        setRegenSelection({ invoice: false, rcti: false });
        setRegenLoading(true);
        setShowRegenModal(true);
        try {
            const res = await regenerateJobDocuments(jobId, { dryRun: true });
            setRegenPreview(res);
            setRegenSelection({
                invoice: !!res?.invoice?.regenerable,
                rcti: !!res?.rcti?.regenerable,
            });
        } catch (error) {
            setRegenPreview({ error: error.message || 'Failed to check documents.' });
        } finally {
            setRegenLoading(false);
        }
    };

    const handleToggleRegen = (docKey) => {
        setRegenSelection(prev => ({ ...prev, [docKey]: !prev[docKey] }));
    };

    const handleRegenerateConfirmed = async () => {
        setRegenSubmitting(true);
        setRegenResult(null);
        try {
            const res = await regenerateJobDocuments(jobId, {
                regenerateInvoice: regenSelection.invoice,
                regenerateRcti: regenSelection.rcti,
            });
            setRegenResult(res);
            await fetchJob();
            fetchAuditLog();
        } catch (error) {
            setRegenResult({ status: 'fail', message: error.message || 'Failed to regenerate documents.' });
        } finally {
            setRegenSubmitting(false);
        }
    };

    const dismissAllocationWarning = () => {
        setAllocationBreach(null);
        setPendingAllocationData(null);
    };

    // Admin has read the warning and is going over deliberately. The override is audited server-side.
    const handleSaveDespiteAllocation = async () => {
        const data = pendingAllocationData;
        dismissAllocationWarning();
        if (data) await onSubmit(data, true, true);
    };

    // --- Possible-duplicate warning before completing a job ---
    const dismissDuplicateWarning = () => {
        setShowDuplicateModal(false);
        setPendingCompletionData(null);
        setDuplicateJobs([]);
    };

    // Admin has read the warning and wants to complete anyway — re-run the same submit, past the check.
    const handleCompleteDespiteDuplicate = async () => {
        const data = pendingCompletionData;
        dismissDuplicateWarning();
        if (data) await onSubmit(data, true);
    };

    const closeRegenModal = () => {
        setShowRegenModal(false);
        setRegenPreview(null);
        setRegenResult(null);
        setRegenLoading(false);
        setRegenChangeSummary(null);
    };

    if (configLoading || loading) {
        return <p className="text-center text-gray-500 py-10">Loading job details...</p>;
    }
    if (configError) {
        return <p className="text-center text-red-500 bg-red-100 p-3 rounded">Error loading configuration: {configError}</p>;
    }
    if (!originalJobData && !loading) {
         return (
            <div className="container mx-auto px-4 py-8 max-w-4xl text-center">
                 <p className="text-red-600 bg-red-100 p-3 rounded mb-4">{apiError || "Job not found."}</p>
                 <Link to={backTarget} state={backState} className="text-jims-cyan-dark hover:text-jims-cyan underline">
                    {backLabel}
                </Link>
            </div>
         );
    }

    return (
        <div className="container mx-auto px-4 pt-8 pb-20 max-w-4xl">
            <div className="flex justify-between items-center mb-2">
                <h1 className="text-3xl font-bold text-jims-cyan-dark">Edit Job</h1>
                <Link 
                    to={backTarget}
                    state={backState}
                    className="text-jims-cyan-dark hover:text-jims-cyan underline"
                >
                    {backLabel}
                </Link>
            </div>
            <div className="mb-6 text-sm text-gray-600">
                Job ID: <span className="font-medium text-gray-800">{jobId}</span>
            </div>

            {/* Wrapped so react-hook-form's submit event is never passed as skipDuplicateCheck. */}
            <form onSubmit={handleSubmit((data) => onSubmit(data))} onKeyDown={handleKeyDown} className="bg-white p-8 rounded-lg shadow-md space-y-6">
                <div ref={errorRef}>
                    {apiError && <p className="text-center text-red-600 bg-red-100 p-3 rounded mb-4">{apiError}</p>}
                    {saveSuccessMessage && <p className="text-center text-green-600 bg-green-100 p-3 rounded mb-4">{saveSuccessMessage}</p>}
                    {statusChangeValidationError && (
                        <p className="text-center text-red-600 bg-red-100 p-3 rounded mb-4 flex items-center justify-center">
                            <FaInfoCircle className="mr-2" /> {statusChangeValidationError}
                        </p>
                    )}
                </div>

                {isOverdue && (
                    <div className="mb-4 pb-3 border-b border-red-200">
                        <p className="text-xs text-red-600 flex items-center">
                            <FaExclamationTriangle className="inline mr-2 flex-shrink-0" />
                            Job is overdue. Contact the franchisee to reschedule or update status to Completed if job was already done.
                        </p>
                    </div>
                )}

                {isServiceDelayed && originalJobData?.reasonForDelay && (
                    <div className="p-3 bg-orange-100 text-orange-700 border border-orange-300 rounded text-sm mb-4 flex items-center">
                        <FaInfoCircle className="inline mr-2 flex-shrink-0" />
                        Delay Reason: {originalJobData.reasonForDelay}
                    </div>
                )}

                {originalJobData?.notesForAdmin && (
                    <div className="p-3 bg-blue-100 text-blue-700 border border-blue-300 rounded text-sm mb-4 flex items-center">
                        <FaInfoCircle className="inline mr-2 flex-shrink-0" />
                        Notes from franchisee: {originalJobData.notesForAdmin}
                    </div>
                )}

                {originalJobData?.isNoPhotoJob && (
                    <div className="p-3 bg-orange-100 text-orange-700 border border-orange-300 rounded text-xs italic mb-4">
                        Franchisee declared that there are no photos for this job.
                    </div>
                )}

                {(originalJobData?.status === 'completed' || originalJobData?.status === 'invoiced' || originalJobData?.invoiceId || originalJobData?.rctiId) && originalJobData?.completionDate && (
                    <div className="p-3 bg-green-50 text-green-700 border border-green-200 rounded text-sm mb-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <FaCalendarCheck className="inline mr-2 flex-shrink-0 text-green-600" />
                                Service Completed On: {formatTimestampForDisplay(originalJobData.completionDate, true)}
                            </div>
                            {!originalJobData.invoiceId || !originalJobData.rctiId ? (
                                <button
                                    type="button"
                                    onClick={openInvoiceRctiModal}
                                    className="ml-4 px-4 py-2 bg-jims-cyan text-white rounded hover:bg-jims-cyan-dark transition-colors duration-200 flex items-center text-sm font-medium"
                                >
                                    <FaFileInvoice className="mr-2" />
                                    {invoiceRctiActionLabel}
                                </button>
                            ) : (
                                <div className="ml-4 flex items-center text-sm font-medium">
                                    <span className="text-gray-600 mr-2">Xero Status:</span>
                                    <span className={`px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getXeroStatusPillClass(displayXeroInvoiceStatus)}`}>
                                        {displayXeroInvoiceStatus || 'Not synced'}
                                    </span>
                                </div>
                            )}
                        </div>
                        {originalJobData.invoiceCreatedAt && (
                            <div className="flex items-center mt-1">
                                <FaFileInvoice className="inline mr-2 flex-shrink-0 text-green-600" />
                                Invoiced On: {formatTimestampForDisplay(originalJobData.invoiceCreatedAt, true)}
                            </div>
                        )}
                        {/* Invoice and RCTI Links with Void actions */}
                        <div className="mt-3 pt-3 border-t border-green-200">
                            {originalJobData.invoiceId || originalJobData.rctiId ? (
                                <div className="space-y-3">
                                    <div className="flex flex-wrap gap-4 items-center">
                                        {originalJobData.invoiceId && (
                                            <div className="inline-flex items-center gap-2">
                                                <Link
                                                    to={`/invoices/${originalJobData.invoiceId}`}
                                                    state={documentReturnState}
                                                    className={`font-medium inline-flex items-center ${isInvoiceVoided ? 'text-red-600 line-through' : 'text-jims-cyan-dark hover:text-jims-cyan'}`}
                                                >
                                                    <FaFileInvoice className="mr-2" />
                                                    View Invoice {originalJobData.invoiceNumber ? `(${originalJobData.invoiceNumber})` : ''}
                                                </Link>
                                                {isInvoiceVoided && (
                                                    <span className="text-xs text-red-600 font-semibold">(Voided)</span>
                                                )}
                                                {canVoidInvoice && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowVoidInvoiceConfirm(true)}
                                                        disabled={voidingInvoice}
                                                        className={`inline-flex items-center transition-colors ${
                                                            voidingInvoice ? 'text-gray-400 cursor-not-allowed' : 'text-red-500 hover:text-red-700'
                                                        }`}
                                                        title="Void Invoice"
                                                    >
                                                        {voidingInvoice ? (
                                                            <FaSync className="animate-spin" size={12} />
                                                        ) : (
                                                            <FaTrashAlt size={12} />
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        {originalJobData.rctiId && (
                                            <div className="inline-flex items-center gap-2">
                                                <Link
                                                    to={`/rctis/${originalJobData.rctiId}`}
                                                    state={documentReturnState}
                                                    className={`font-medium inline-flex items-center ${isRctiVoided ? 'text-red-600 line-through' : 'text-jims-cyan-dark hover:text-jims-cyan'}`}
                                                >
                                                    <FaFileInvoice className="mr-2" />
                                                    View RCTI {originalJobData.rctiNumber ? `(${originalJobData.rctiNumber})` : ''}
                                                </Link>
                                                {isRctiVoided && (
                                                    <span className="text-xs text-red-600 font-semibold">(Voided)</span>
                                                )}
                                                {canVoidRCTI && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowVoidRCTIConfirm(true)}
                                                        disabled={voidingRCTI}
                                                        className={`inline-flex items-center transition-colors ${
                                                            voidingRCTI ? 'text-gray-400 cursor-not-allowed' : 'text-red-500 hover:text-red-700'
                                                        }`}
                                                        title="Void RCTI"
                                                    >
                                                        {voidingRCTI ? (
                                                            <FaSync className="animate-spin" size={12} />
                                                        ) : (
                                                            <FaTrashAlt size={12} />
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {invoiceGenerationFailureInfo && (
                                        <div className="rounded border border-red-200 bg-red-50 p-3">
                                            <p className="text-sm text-red-700 flex items-center">
                                                <FaExclamationTriangle className="mr-2 flex-shrink-0" />
                                                {invoiceGenerationFailureInfo.userFriendlyMessage}
                                            </p>
                                            {invoiceGenerationFailureInfo.technicalMessages.length > 0 && (
                                                <div className="mt-2 text-xs text-gray-600 italic space-y-1">
                                                    {invoiceGenerationFailureInfo.technicalMessages.map((message, index) => (
                                                        <p key={`${message}-${index}`}>{message}</p>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                invoiceGenerationFailureInfo ? (
                                    <div className="rounded border border-red-200 bg-red-50 p-3">
                                        <p className="text-sm text-red-700 flex items-center">
                                            <FaExclamationTriangle className="mr-2 flex-shrink-0" />
                                            {invoiceGenerationFailureInfo.userFriendlyMessage}
                                        </p>
                                        {invoiceGenerationFailureInfo.technicalMessages.length > 0 && (
                                            <div className="mt-2 text-xs text-gray-600 italic space-y-1">
                                                {invoiceGenerationFailureInfo.technicalMessages.map((message, index) => (
                                                    <p key={`${message}-${index}`}>{message}</p>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-xs text-orange-600 italic flex items-center">
                                        <FaInfoCircle className="mr-2" />
                                        There is no invoice and RCTI created for this job
                                    </p>
                                )
                            )}
                        </div>
                    </div>
                )}

                <div className="relative">
                    <label htmlFor="fseNameSearch" className="block text-sm font-medium text-gray-700">Franchisee *</label>
                    <input id="fseNameSearch" type="text" autoComplete="off"
                        {...register("fseNameSearch", { required: "Franchisee search is required." })}
                        onChange={fseHandlers.onChange}
                        onBlur={() => setTimeout(() => franchiseeSearch.setShowSuggestions(false), 150)}
                        onFocus={() => franchiseeSearch.suggestions.length > 0 && franchiseeSearch.setShowSuggestions(true)}
                        className={`mt-1 block w-full input-field ${errors.fseNameSearch || (errors.fseId && !watch('fseId')) ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    <input type="hidden" {...register("fseId", { required: "Please select a franchisee." })} />
                    {errors.fseId && !watch('fseId') && <p className="input-error">{errors.fseId.message}</p>}
                    {franchiseeSearch.showSuggestions && (
                        <ul className="suggestions-list">
                            {franchiseeSearch.isSearching ? <li className="searching">Searching...</li> :
                             franchiseeSearch.suggestions.length > 0 ? franchiseeSearch.suggestions.map(item => (
                                <li key={item.id} className="suggestion-item" onMouseDown={() => fseHandlers.onSelect(item)}>
                                    {fseHandlers.renderSuggestions(item)}
                                </li>
                            )) : franchiseeSearch.searchTerm.length >= 3 && <li className="no-results">No franchisees found.</li>}
                        </ul>
                    )}
                </div>                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-500">{terminology.client.singular}</label>
                        <p className="mt-1 p-2 bg-gray-100 border border-gray-300 rounded text-sm text-gray-700">{watch('clientName')}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-500">{terminology.household.singular}</label>
                        <p className="mt-1 p-2 bg-gray-100 border border-gray-300 rounded text-sm text-gray-700">{watch('householdName')}</p>
                        <p className="mt-1 text-xs text-gray-500">
                            <FaMapMarkerAlt className="inline mr-1"/> {watch('householdAddress')}
                        </p>
                    </div>
                </div>

                {/* NDIS Field Display */}
                {originalJobData?.ndisJob && (
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-500 mb-2">NDIS Service</label>
                        <div className="p-2 bg-green-50 border border-green-200 rounded text-sm flex items-center">
                            <FaMedkit className="mr-2 text-green-600" />
                            <span className="text-green-700 font-medium">This is an NDIS service job</span>
                        </div>
                    </div>
                )}

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Services *</label>
                    {serviceTypesLoading ? (
                        <p className="text-gray-500">Loading services...</p>
                    ) : allServiceTypes.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                            {allServiceTypes.map(service => {
                                const isSelected = selectedServiceIds.includes(service.id);
                                return (
                                    <button
                                        key={service.id}
                                        type="button"
                                        onClick={() => handleServiceToggle(service.id)}
                                        className={`
                                            px-3 py-1 rounded-full text-sm font-medium border transition-colors duration-150
                                            flex items-center justify-center
                                            ${isSelected
                                                ? 'bg-jims-cyan text-white border-jims-cyan'
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                                            }
                                        `}
                                    >
                                        {isSelected && <FaCheck className="mr-1 text-xs" />}
                                        {service.name}
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="text-gray-500">No services available.</p>
                    )}
                    {serviceSelectionError && <p className="input-error mt-2">{serviceSelectionError}</p>}
                    <input type="hidden" {...register('servicesDirty')} />
                </div>

                {/* Job Frequency Display (read-only) */}
                {originalJobData && (
                    <div>
                        <label className="block text-sm font-medium text-gray-500 mb-2">Job Frequency</label>
                        <div className="p-3 bg-gray-100 border border-gray-300 rounded text-sm text-gray-700 flex items-center">
                            <FaCalendarAlt className="mr-2 text-jims-cyan" />
                            {originalJobData.scheduleType === 'one-off' ? (
                                <span>One-off</span>
                            ) : originalJobData.scheduleType === 'recurring' ? (
                                <span>
                                    Recurring: {formatters.formatFrequency(originalJobData.frequencyType, originalJobData.frequencyValue)}
                                </span>
                            ) : (
                                <span>{originalJobData.scheduleType || 'N/A'}</span>
                            )}
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                    {/* Allocation Type Pills */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Allocation Type *
                        </label>
                        <div className="flex space-x-4">
                            {allocationTypes.map(type => (
                                <label key={type.value} className="inline-flex items-center">
                                    <input
                                        type="radio"
                                        value={type.value}
                                        {...register("allocationType", { required: "Please select an allocation type." })}
                                        className="sr-only"
                                    />
                                    <div className={`px-4 py-2 rounded-full border-2 cursor-pointer transition-all ${
                                        allocationType === type.value 
                                            ? 'border-blue-500 bg-blue-50 text-blue-700' 
                                            : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                    }`}>
                                        {type.label}
                                    </div>
                                </label>
                            ))}
                        </div>
                        {errors.allocationType && <p className="input-error">{errors.allocationType.message}</p>}
                    </div>
                    <div>
                        <label htmlFor="allotedHours" className="block text-sm font-medium text-gray-700">
                            {allocationType === 'fixed-fee' ? 'Fixed allocated hours' : 'Allotted Hours'}
                        </label>
                        {/* The input's own border is the separation — the allocation frequency just
                            sits alongside it as plain text, e.g. [2] Every 14 Days */}
                        <div className="mt-1 flex items-center gap-3">
                            <input id="allotedHours" type="number" step="0.01" min="0"
                                {...register("allotedHours", {
                                    valueAsNumber: true,
                                    min: { value: 0, message: "Cannot be negative." }
                                 })}
                                className={`${hoursInputClass} border rounded-md focus:border-jims-cyan ${errors.allotedHours ? 'border-red-500' : 'border-gray-300'}`}
                                placeholder="1.5"
                            />
                            {showAllocationFrequency && (
                                <span className="whitespace-nowrap text-sm text-gray-500">
                                    {allocationFrequencyLabel}
                                </span>
                            )}
                        </div>
                        {errors.allotedHours && <p className="input-error">{errors.allotedHours.message}</p>}
                    </div>
                    <div>
                        <label htmlFor="actualHours" className="block text-sm font-medium text-gray-700">Actual Hours {currentStatus === 'completed' && allocationType !== 'fixed-fee' ? '*' : ''}</label>
                        <input id="actualHours" type="number" step="0.01" min="0"
                            {...register("actualHours", {
                                valueAsNumber: true,
                                min: { value: 0, message: "Cannot be negative." }
                             })}
                            className={`mt-1 ${hoursInputClass} border rounded-md focus:border-jims-cyan ${errors.actualHours ? 'border-red-500' : 'border-gray-300'}`}
                            placeholder="1.0"
                        />
                        {errors.actualHours && <p className="input-error">{errors.actualHours.message}</p>}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
                    <div>
                        <label htmlFor="priceQuotePerHour" className="block text-sm font-medium text-gray-700">FSE Quote /hour</label>
                        <input id="priceQuotePerHour" type="number" step="0.01"
                            {...register("priceQuotePerHour")}
                            readOnly
                            className="mt-1 block w-full input-field bg-gray-100 cursor-not-allowed border-gray-300"
                            placeholder="N/A"
                        />
                    </div>
                    <div>
                        <label htmlFor="pricePerHour" className="block text-sm font-medium text-gray-700">FSE price/hour (w/ GST)</label>
                        <input id="pricePerHour" type="number" step="0.01" min="0"
                            {...register("pricePerHour", {
                                valueAsNumber: true,
                                min: { value: 0, message: "Cannot be negative." }
                             })}
                            className={`mt-1 block w-full input-field ${errors.pricePerHour ? 'border-red-500' : 'border-gray-300'}`}
                            placeholder="e.g., 55.00"
                        />
                        {errors.pricePerHour && <p className="input-error">{errors.pricePerHour.message}</p>}
                    </div>
                    <div>
                        <label htmlFor="additionalPrice" className="block text-sm font-medium text-gray-700">Invoice Price (ex. GST)</label>
                        <input id="additionalPrice" type="number" step="0.01" min="0"
                            {...register("additionalPrice", {
                                valueAsNumber: true,
                                min: { value: 0, message: "Cannot be negative." }
                             })}
                            className={`mt-1 block w-full input-field ${errors.additionalPrice ? 'border-red-500' : 'border-gray-300'}`}
                            placeholder="e.g., 77.00"
                        />
                        {errors.additionalPrice && <p className="input-error">{errors.additionalPrice.message}</p>}
                    </div>
                    <div>
                        <label htmlFor="invoicePricePerHour" className="block text-sm font-medium text-gray-700">Invoice price/hour (w/ GST)</label>
                        <input id="invoicePricePerHour" type="number" step="0.01" min="0"
                            {...register("invoicePricePerHour", {
                                valueAsNumber: true,
                                min: { value: 0, message: "Cannot be negative." }
                             })}
                            className={`mt-1 block w-full input-field ${errors.invoicePricePerHour ? 'border-red-500' : 'border-gray-300'}`}
                            placeholder="e.g., 60.50"
                        />
                        {errors.invoicePricePerHour && <p className="input-error">{errors.invoicePricePerHour.message}</p>}
                    </div>
                </div>

                <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description (Invoice Notes)</label>
                    <textarea id="description"
                        {...register("description")}
                        rows="3"
                        className={`mt-1 block w-full input-field ${errors.description ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Enter notes that will appear on the client invoice..."
                    />
                    {errors.description && <p className="input-error">{errors.description.message}</p>}
                </div>

                <div>
                    <label htmlFor="adminNotes" className="block text-sm font-medium text-gray-700">Admin Notes</label>
                    <textarea id="adminNotes"
                        {...register("adminNotes")}
                        rows="3"
                        className={`mt-1 block w-full input-field ${errors.adminNotes ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Internal notes for admin use only..."
                    />
                    {errors.adminNotes && <p className="input-error">{errors.adminNotes.message}</p>}
                </div>

                <div>
                    <label htmlFor="notesForFranchisee" className="block text-sm font-medium text-gray-700">Notes for franchisee</label>
                    <textarea id="notesForFranchisee"
                        {...register("notesForFranchisee")}
                        rows="3"
                        className={`mt-1 block w-full input-field ${errors.notesForFranchisee ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Enter any specific instructions or notes for the franchisee..."
                    />
                    {errors.notesForFranchisee && <p className="input-error">{errors.notesForFranchisee.message}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                    <div>
                        <label htmlFor="status" className="block text-sm font-medium text-gray-700">Status *</label>
                        <select id="status"
                            {...register("status", { required: "Status is required." })}
                            className={`mt-1 block w-full input-field ${errors.status ? 'border-red-500' : 'border-gray-300'}`}
                            disabled={jobStatuses.length === 0}
                        >
                            <option value="">Select Status...</option>
                            {jobStatuses.map(statusOption => (
                                <option key={statusOption.code} value={statusOption.code}>
                                    {statusOption.display}
                                </option>
                            ))}
                        </select>
                        {errors.status && <p className="input-error">{errors.status.message}</p>}
                        {jobStatuses.length === 0 && !configLoading && <p className="input-error">Could not load status options.</p>}
                    </div>
                    <div>
                        <label htmlFor="completionDate" className="block text-sm font-medium text-gray-700">
                            Completion Date {currentStatus === 'completed' ? '*' : ''}
                        </label>
                        <input id="completionDate" type="date"
                            max={new Date().toISOString().split('T')[0]}
                            {...register("completionDate", {
                                required: currentStatus === 'completed' ? "Completion date is required when status is completed." : false,
                                validate: value => {
                                    if (value && value > new Date().toISOString().split('T')[0]) {
                                        return 'Completion date cannot be a future date.';
                                    }
                                    return true;
                                }
                            })}
                            className={`mt-1 block w-full input-field ${errors.completionDate ? 'border-red-500' : 'border-gray-300'}`}
                        />
                        {errors.completionDate && <p className="input-error">{errors.completionDate.message}</p>}
                    </div>
                    <div>
                        <label htmlFor="invoiceStatusDisplay" className="block text-sm font-medium text-gray-500">Xero Invoice Status</label>
                        <p id="invoiceStatusDisplay" className="mt-1 p-2 bg-gray-100 border border-gray-300 rounded text-sm text-gray-700">
                            {displayXeroInvoiceStatus || 'N/A'}
                        </p>
                    </div>
                </div>

               <div className="fixed bottom-0 left-0 right-0 z-10 bg-white border-t border-gray-200 shadow-[0_-2px_6px_rgba(0,0,0,0.08)]">
                      <div className="container mx-auto px-6 py-3 max-w-4xl flex justify-end gap-3">
                    <Link
                        to={backTarget}
                        state={backState}
                        className="btn btn-secondary"
                    >
                        Cancel
                    </Link>
                    <button type="submit" className="btn btn-primary" disabled={isSubmitting || franchiseeSearch.isSearching || (!isDirty && !watch('servicesDirty'))}>
                        {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </button>
                    </div>
                </div>

                <style jsx>{`
                    .input-field { padding: 0.5rem 0.75rem; border-width: 1px; border-radius: 0.375rem; box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); outline: none; transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out; }
                    .input-field:focus { border-color: #06B6D4; box-shadow: 0 0 0 3px rgba(6, 182, 212, 0.3); }
                    .input-error { margin-top: 0.25rem; font-size: 0.75rem; color: #EF4444; }
                    .suggestions-list { position: absolute; z-index: 10; width: 100%; background-color: white; border: 1px solid #D1D5DB; border-radius: 0.375rem; margin-top: 0.25rem; max-height: 15rem; overflow-y: auto; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06); }
                    .suggestion-item { padding: 0.5rem 0.75rem; cursor: pointer; }
                    .suggestion-item:hover { background-color: #67E8F9; }
                    .searching, .no-results { padding: 0.5rem 0.75rem; color: #6B7280; }
                    .btn { padding: 0.5rem 1rem; font-weight: 700; border-radius: 0.375rem; transition: background-color 0.3s ease, opacity 0.3s ease; border: 1px solid transparent; cursor: pointer; }
                    .btn:disabled { opacity: 0.5; cursor: not-allowed; }
                    .btn-primary { background-color: #06B6D4; color: white; }
                    .btn-primary:hover:not(:disabled) { background-color: #0E7490; }
                    .btn-secondary { background-color: #E5E7EB; color: #1F2937; }
                    .btn-secondary:hover:not(:disabled) { background-color: #D1D5DB; }
                `}</style>
            </form>

            {/* Activity Log Section */}
            {originalJobData && (
                <ActivityLog
                    entries={auditLog}
                    loading={auditLogLoading}
                    expanded={auditLogExpanded}
                    onToggle={() => setAuditLogExpanded(!auditLogExpanded)}
                    createdAt={originalJobData.createdAt}
                    createdLabel="Job created"
                    createdDescription="Job record created in the system."
                >
                    {(originalJobData.completionDate || originalJobData.invoiceCreatedAt) && (
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {originalJobData.completionDate && (
                                <div className="bg-green-50 border border-green-100 rounded p-3">
                                    <p className="text-xs font-medium text-gray-500">Service Completed On</p>
                                    <p className="text-sm font-semibold text-green-700">{formatTimestampForDisplay(originalJobData.completionDate, true)}</p>
                                </div>
                            )}
                            {originalJobData.invoiceCreatedAt && (
                                <div className="bg-blue-50 border border-blue-100 rounded p-3">
                                    <p className="text-xs font-medium text-gray-500">Invoiced On</p>
                                    <p className="text-sm font-semibold text-blue-700">{formatTimestampForDisplay(originalJobData.invoiceCreatedAt, true)}</p>
                                </div>
                            )}
                        </div>
                    )}
                </ActivityLog>
            )}

            {/* Completion Progress Modal */}
            <CompletionProgressModal
                isOpen={showCompletionProgress}
                currentStep={completionStep}
                stepResults={completionStepResults}
                warnings={completionWarnings}
                onClose={() => {
                    setShowCompletionProgress(false);
                    setCompletionStep('saving');
                    setCompletionStepResults({});
                    setCompletionWarnings([]);
                }}
            />

            {/* Invoice & RCTI Creation Modal */}
            {showInvoiceRctiModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                            <FaFileInvoice className="mr-2 text-jims-cyan" />
                            {invoiceRctiActionLabel}
                        </h3>
                        
                        {!invoiceRctiResult ? (
                            <>
                                <p className="text-gray-600 mb-6">
                                    This will manually {invoiceRctiActionLabel.toLowerCase()} for this completed job,
                                    bypassing automatic creation rules. The RCTI PDF is emailed to the franchisee as
                                    part of this, and the result will show the email address used.
                                    {originalJobData?.allocationType === 'monthly' && (
                                        <span className="block mt-2 text-sm text-orange-600">
                                            <FaInfoCircle className="inline mr-1" />
                                            Note: This is a monthly allocation job. Invoice/RCTI are normally created at month-end.
                                        </span>
                                    )}
                                </p>
                                <p className="text-sm text-gray-500 mb-6">
                                    Job ID: <span className="font-mono font-medium">{jobId}</span>
                                </p>
                                
                                <div className="flex justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={closeInvoiceRctiModal}
                                        disabled={invoiceRctiCreating}
                                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCreateInvoiceAndRcti}
                                        disabled={invoiceRctiCreating}
                                        className="px-4 py-2 bg-jims-cyan text-white rounded hover:bg-jims-cyan-dark transition-colors disabled:opacity-50 flex items-center"
                                    >
                                        {invoiceRctiCreating ? (
                                            <>Creating...</>
                                        ) : (
                                            <>
                                                <FaFileInvoice className="mr-2" />
                                                {invoiceRctiActionLabel}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                {invoiceRctiResult.status === 'success' && (
                                    <div className="mb-4 p-3 bg-green-100 text-green-700 border border-green-300 rounded flex items-start">
                                        <FaCheck className="mr-2 mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="font-medium mb-2">Successfully created!</p>
                                            {(invoiceRctiResult.invoice?.invoiceId || invoiceRctiResult.invoiceId) && (
                                                <p className="text-sm">Invoice ID: {invoiceRctiResult.invoice?.invoiceId || invoiceRctiResult.invoiceId}</p>
                                            )}
                                            {(invoiceRctiResult.rcti?.rctiId || invoiceRctiResult.rctiId) && (
                                                <p className="text-sm">RCTI ID: {invoiceRctiResult.rcti?.rctiId || invoiceRctiResult.rctiId}</p>
                                            )}
                                            <RctiEmailOutcome email={invoiceRctiResult.rcti?.email} />
                                        </div>
                                    </div>
                                )}

                                {invoiceRctiResult.status === 'partial' && (
                                    <div className="mb-4 p-3 bg-yellow-100 text-yellow-700 border border-yellow-300 rounded">
                                        <p className="font-medium mb-2 flex items-center">
                                            <FaExclamationTriangle className="mr-2" />
                                            Partially successful
                                        </p>
                                        <div className="text-sm space-y-1">
                                            {(invoiceRctiResult.invoice?.invoiceId || invoiceRctiResult.invoiceId) && (
                                                <p className="text-green-700">✓ Invoice created: {invoiceRctiResult.invoice?.invoiceId || invoiceRctiResult.invoiceId}</p>
                                            )}
                                            {(invoiceRctiResult.invoice?.status === 'fail' || invoiceRctiResult.invoiceError) && (
                                                <p className="text-red-700">✗ Invoice failed: {invoiceRctiResult.invoice?.message || invoiceRctiResult.invoiceError}</p>
                                            )}
                                            {(invoiceRctiResult.rcti?.rctiId || invoiceRctiResult.rctiId) && (
                                                <p className="text-green-700">✓ RCTI created: {invoiceRctiResult.rcti?.rctiId || invoiceRctiResult.rctiId}</p>
                                            )}
                                            {(invoiceRctiResult.rcti?.status === 'fail' || invoiceRctiResult.rctiError) && (
                                                <p className="text-red-700">✗ RCTI failed: {invoiceRctiResult.rcti?.message || invoiceRctiResult.rctiError}</p>
                                            )}
                                            <RctiEmailOutcome email={invoiceRctiResult.rcti?.email} />
                                        </div>
                                    </div>
                                )}
                                
                                {invoiceRctiResult.status === 'fail' && (
                                    <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded flex items-start">
                                        <FaExclamationTriangle className="mr-2 mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="font-medium mb-2">Failed to create</p>
                                            <p className="text-sm">{invoiceRctiResult.message || 'Unknown error occurred'}</p>
                                        </div>
                                    </div>
                                )}
                                
                                <div className="flex justify-end">
                                    <button
                                        type="button"
                                        onClick={closeInvoiceRctiModal}
                                        className="px-4 py-2 bg-jims-cyan text-white rounded hover:bg-jims-cyan-dark transition-colors"
                                    >
                                        Close
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Void Invoice Confirmation Modal */}
            <ConfirmationModal
                isOpen={showVoidInvoiceConfirm}
                onClose={() => setShowVoidInvoiceConfirm(false)}
                onConfirm={handleVoidInvoice}
                title="Void Invoice"
                message={`Are you sure you want to void invoice ${originalJobData?.invoiceNumber || ''}? This action cannot be undone. If the invoice has been sent to Xero, it will also be voided there.`}
                confirmText="Void Invoice"
                variant="danger"
                isLoading={voidingInvoice}
                loadingText="Voiding..."
            />

            {/* Void RCTI Confirmation Modal */}
            <ConfirmationModal
                isOpen={showVoidRCTIConfirm}
                onClose={() => setShowVoidRCTIConfirm(false)}
                onConfirm={handleVoidRCTI}
                title="Void RCTI"
                message={`Are you sure you want to void RCTI ${originalJobData?.rctiNumber || ''}? This action cannot be undone. If the RCTI has been sent to Xero, it will also be voided there.`}
                confirmText="Void RCTI"
                variant="danger"
                isLoading={voidingRCTI}
                loadingText="Voiding..."
            />

            {/* Regenerate invoice/RCTI after a price change */}
            <RegenerateDocsModal
                isOpen={showRegenModal}
                loading={regenLoading}
                preview={regenPreview}
                selection={regenSelection}
                onToggle={handleToggleRegen}
                submitting={regenSubmitting}
                result={regenResult}
                onConfirm={handleRegenerateConfirmed}
                onClose={closeRegenModal}
                changeSummary={regenChangeSummary}
            />

            <VoidNoticeModal {...voidNoticeProps} />

            <DuplicateJobWarningModal
                isOpen={showDuplicateModal}
                duplicates={duplicateJobs}
                submitting={isSubmitting}
                jobSummary={{
                    householdName: `${originalJobData?.householdInfo?.firstName || ''} ${originalJobData?.householdInfo?.lastName || ''}`.trim(),
                    address: originalJobData?.householdInfo?.addressLine1,
                    services: (originalJobData?.services || []).map(s => s.name).filter(Boolean).join(', '),
                    completionDate: pendingCompletionData?.completionDate,
                }}
                onConfirm={handleCompleteDespiteDuplicate}
                onCancel={dismissDuplicateWarning}
            />

            <AllocationOverrideModal
                isOpen={!!allocationBreach}
                breach={allocationBreach}
                submitting={isSubmitting}
                onConfirm={handleSaveDespiteAllocation}
                onCancel={dismissAllocationWarning}
            />
        </div>
    );
};

export default EditJobPage;
